// What a new password has to be. The rules, and why they are these, are explained at the top of
// server/utils/passwordPolicy.js, which is the original: the block between the SHARED markers is a
// verbatim copy of it, and server/tests/passwordPolicy.test.js fails if the two differ. Edit it there
// and copy it across. The server checks the same rules, so this copy is what drives the checklist
// under the password field, not what decides.

// BEGIN SHARED
const PASSWORD_MIN_LENGTH = 8;

// Words people build passwords around. A password that is one of these with numbers or symbols
// stuck on the front or back ("Password1!", "qwerty2024", "udal123!") is refused, and so is one
// written with the usual swaps (p@ssw0rd).
const COMMON_WORDS = [
  'password', 'passwort', 'motdepasse', 'contrasena', 'qwerty', 'qwertz', 'azerty', 'asdf', 'asdfgh',
  'asdfghjkl', 'zxcvbn', 'zxcvbnm', 'qwertyuiop', 'abc', 'abcd', 'abcdef', 'abcdefg', 'abcdefgh',
  'iloveyou', 'loveyou', 'love', 'welcome', 'hello', 'hellohello', 'admin', 'administrator', 'root',
  'user', 'login', 'letmein', 'secret', 'master', 'monkey', 'dragon', 'shadow', 'sunshine',
  'princess', 'football', 'baseball', 'soccer', 'superman', 'batman', 'trustno', 'freedom',
  'whatever', 'starwars', 'pokemon', 'michael', 'jordan', 'charlie', 'test', 'tester', 'guest',
  'changeme', 'default', 'computer', 'internet', 'samsung', 'iphone', 'google', 'facebook',
  'udal', 'udalapp', 'parksphere', 'parking', 'carpark', 'mypassword', 'newpassword', 'pass',
  'kodikos', 'agapi', 'elláda', 'ellada', 'hellas', 'olympiakos', 'panathinaikos', 'paok', 'aek',
];

// Whole passwords that the rules above do not already catch.
const COMMON_PASSWORDS = [
  '12345678', '123456789', '1234567890', '0123456789', '87654321', '987654321', '11111111',
  '00000000', '12341234', '11223344', '12121212', '123123123', '1q2w3e4r', '1q2w3e4r5t', 'q1w2e3r4',
  '1qaz2wsx', 'zaq12wsx', '1qazxsw2', 'qazwsx123', '!qaz2wsx', 'a1b2c3d4', 'aa123456', 'qwe123qwe',
  '123qwe123', '1234qwer', 'qwer1234', 'asdf1234', '1234asdf', 'zxcv1234', '147258369', '159753456',
  '741852963', '789456123', '123654789', '666666666', '7777777', '88888888', '99999999', '55555555',
];

// A letter is a character with an upper and a lower case — Latin, Greek, Cyrillic alike. Written out
// rather than as a Unicode property regex, which not every JavaScript engine the app and the web run
// on supports, and a rule that throws on one of them would quietly stop the checklist working there.
const isLetter = (c) => c.toLowerCase() !== c.toUpperCase();
// Numbers and symbols trimmed off the ends; `fromStart` false trims only the end.
const trimNonLetters = (s, fromStart = true) => {
  const chars = [...s];
  let a = 0;
  let b = chars.length;
  if (fromStart) while (a < b && !isLetter(chars[a])) a += 1;
  while (b > a && !isLetter(chars[b - 1])) b -= 1;
  return chars.slice(a, b).join('');
};

const LEET = { '@': 'a', '4': 'a', '8': 'b', '3': 'e', '1': 'i', '!': 'i', '0': 'o', '$': 's', '5': 's', '7': 't', '+': 't' };

const isRunOf = (s) => /^(.)\1+$/.test(s);
const isSequence = (s) => {
  if (s.length < 4) return false;
  const codes = [...s].map((c) => c.charCodeAt(0));
  const step = codes[1] - codes[0];
  return (step === 1 || step === -1) && codes.every((c, i) => i === 0 || c - codes[i - 1] === step);
};

function isCommonPassword(password) {
  const pw = password.toLowerCase();
  if (COMMON_PASSWORDS.includes(pw) || isRunOf(pw) || isSequence(pw)) return true;
  // The letters at its heart, three ways: numbers and symbols trimmed off both ends ("password" in
  // "Password1!"); the trailing ones trimmed BEFORE undoing the usual swaps, so a final "1" is not
  // read as an "i" ("p@ssw0rd1", "@dmin2024"); and the trimmed core with the swaps undone.
  const unleet = (s) => [...s].map((c) => LEET[c] || c).join('');
  const core = trimNonLetters(pw);
  const candidates = [core, trimNonLetters(unleet(trimNonLetters(pw, false))), unleet(core)];
  return candidates.some((w) => w && (COMMON_WORDS.includes(w) || isRunOf(w) || isSequence(w)));
}

// Your own username or the name part of your email, anywhere in it — the first thing anyone who
// knows you would try.
function containsPersonalInfo(password, { username, email } = {}) {
  const pw = password.toLowerCase();
  const local = typeof email === 'string' ? email.split('@')[0] : '';
  return [username, local]
    .filter((s) => typeof s === 'string' && s.trim().length >= 3)
    .some((s) => pw.includes(s.trim().toLowerCase()));
}

const PASSWORD_RULES = [
  { id: 'length', label: `At least ${PASSWORD_MIN_LENGTH} characters`, test: (pw) => [...pw].length >= PASSWORD_MIN_LENGTH },
  { id: 'variety', label: 'A number or symbol', test: (pw) => [...pw].some((c) => !isLetter(c)) },
  { id: 'common', label: 'Not a common or easy-to-guess password', test: (pw, who) => pw.length > 0 && !isCommonPassword(pw) && !containsPersonalInfo(pw, who) },
];

/**
 * Checks a new password. `who` is { username, email } where known, for the personal-info rule.
 * @returns {{ ok: boolean, rules: {id: string, label: string, met: boolean}[] }}
 */
function checkPassword(password, who = {}) {
  const pw = typeof password === 'string' ? password : '';
  const rules = PASSWORD_RULES.map((r) => ({ id: r.id, label: r.label, met: r.test(pw, who) }));
  return { ok: rules.every((r) => r.met), rules };
}

/** One sentence for an error message, naming the first rule that is not met. */
function passwordProblem(password, who = {}) {
  const missed = checkPassword(password, who).rules.find((r) => !r.met);
  if (!missed) return null;
  if (missed.id === 'length') return `The password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  if (missed.id === 'variety') return 'The password needs at least one number or symbol.';
  return 'That password is too common or too easy to guess. Please choose another.';
}
// END SHARED

export { PASSWORD_MIN_LENGTH, PASSWORD_RULES, checkPassword, passwordProblem };
